import { action, computed, observable } from 'mobx';

import {
  checkAnchorsBroadcastStatus,
  checkFanZoneStatus,
  fetchGalleriesAndVideosCountForAllAnchors,
  getAnchors,
} from '../pages/Anchors/services';
import NNL_LOGO from '../assets/NNLiveAds/ad-nnlive-desktop.png';
import { EXPIRY_TIMES_IN_MS, withCacheStorage } from '../utils/withCache';


const BASE_ROUTE = '/naked-news-anchor';

const NAKED_NEWS_LIVE_DATA = {
  id: 241,
  name: 'Naked News Live',
  biopic: NNL_LOGO,
};

export const ANCHORS_LIST = [
  {
    id: 64,
    name: 'Eila Adams',
  },
  {
    id: 114,
    name: 'Frankie Kennedy',
  },
  {
    id: 72,
    name: 'Isabella Rossini',
  },

  {
    id: 1277,
    name: 'Hanna Orio',
  },
  {
    id: 47884,
    name: 'Yoyo',
  },
  {
    id: 79,
    name: 'Madison Banes',
  },
  {
    id: 28045,
    name: 'Bree',
  },
  {
    id: 79296,
    name: 'Sarah',
  },
  {
    id: 244,
    name: 'Cecelia',
  },
  {
    id: 47882,
    name: 'Angel',
  },
  {
    id: 107,
    name: 'Tia',
  },
  {
    id: 104,
    name: 'Alana',
  },
  {
    id: 74530,
    name: 'Erica Stevens',
  },
  {
    id: 1474,
    name: 'Taylor Lee',
  },
];

class AnchorStore {
  @observable isAnchorLive = undefined;
  @observable isNakedNewsLive = false;
  @observable allCurrentAndGuestAnchors = [];
  @observable anchors = {
    currentAnchors: [],
    guestAnchors: [],
    pastAnchors: [],
  };

  @observable anchorsWithFanzone = [];
  @observable onlineAnchors = {};

  @computed get allAnchors() {
    return [
      ...this.anchors?.currentAnchors,
      ...this.anchors?.guestAnchors,
      ...this.anchors?.pastAnchors,
    ];
  }

  @observable mediaTotalForAnchors = {};

  @computed get mergedFanzoneAndAnchorData() {
    const mergedAnchorData = [];

    this.anchorsWithFanzone.forEach((anchor) => {
      const anchorData = {
        ...anchor,
        ...this.allAnchors?.find(
          (anchorsData) => anchorsData?.id === anchor.id
        ),
      };
      mergedAnchorData.push(anchorData);
    });

    return mergedAnchorData;
  }

  @action setIsAnchorLive = (value) => {
    this.isAnchorLive = value;
  };

  @action setIsNakedNewsLive = (value) => {
    this.isNakedNewsLive = value;
  };

  @action setAnchorsWithFanzone = async() => {
    if (this.anchorsWithFanzone?.length) {
      return;
    }

    const [{ error, data }] = await withCacheStorage([
      {
        cacheKey: 'fan-zone',
        fn: checkFanZoneStatus,
        expiryTime: EXPIRY_TIMES_IN_MS['2_HOUR'],
      },
    ]);
    if (!error) {
      this.anchorsWithFanzone = [...data?.data, NAKED_NEWS_LIVE_DATA];
      Promise.resolve(data);
    }
  };

  @action setAnchors = async() => {
    if (this.anchors?.currentAnchors?.length) {
      return;
    }
    const [{ error, data }] = await withCacheStorage([
      {
        cacheKey: 'anchors-response',
        fn: getAnchors,
        expiryTime: EXPIRY_TIMES_IN_MS['1_HOUR'],
      },
    ]);

    if (!error) {
      const anchors = data.data;
      anchors.currentAnchors.push(NAKED_NEWS_LIVE_DATA);
      this.allCurrentAndGuestAnchors = [
        ...anchors.currentAnchors,
        ...anchors.guestAnchors,
      ];
      this.anchors = anchors;
      Promise.resolve(data);
    }
  };

  @action getAnchorData = (id) => {
    const currentAnchor = this.anchors?.currentAnchors?.find(
      (anchor) => parseInt(id, 10) === anchor?.id
    );
    const guestAnchor = this.anchors?.guestAnchors?.find(
      (anchor) => parseInt(id, 10) === anchor?.id
    );
    const pastAnchor = this.anchor?.pastAnchors?.find(
      (anchor) => parseInt(id, 10) === anchor?.id
    );

    return currentAnchor || guestAnchor || pastAnchor;
  };

  sortAnchorByLiveStatus = (anchors, onlineAnchors) => {
    const liveAnchors = [];
    const offlineAnchors = [];

    anchors.forEach((anchor) => {
      if (onlineAnchors[anchor.id] !== undefined) {
        liveAnchors.push(anchor);
      } else {
        offlineAnchors.push(anchor);
      }
    });

    return liveAnchors.concat(offlineAnchors);
  };

  @action getAnchorsLiveStatus = async() => {
    const { error, data } = await checkAnchorsBroadcastStatus();
    if (!error) {
      const onlineAnchorsMap = {};
      data.data.forEach(
        (anchor) => (onlineAnchorsMap[anchor.id] = anchor?.name)
      );
      this.onlineAnchors = onlineAnchorsMap;
    }

    return [];
  };

  @computed get anchorsSortedByLiveStatus() {
    return this.sortAnchorByLiveStatus(
      this.mergedFanzoneAndAnchorData,
      this.onlineAnchors
    );
  }

  @computed get mammeoAnchors() {
    const anchorsList = this.allAnchors?.filter((anchor) =>
      ANCHORS_LIST.some((item) => item.id === anchor.id)
    );

    return anchorsList;
  }

  @action
    setAvailableMediaTotals = (responseData) => {
      this.mediaTotalForAnchors = responseData;
    };

  fetchAvailableMediaTotals = async() => {
    const { error, data } = await fetchGalleriesAndVideosCountForAllAnchors();
    if (!error) {
      this.setAvailableMediaTotals(data?.data);
    }
  };

  getMediaTotalForAnchor = (id) => this.mediaTotalForAnchors?.[id];

  anchorHasFanzone = (anchorId) => {
    for (let i = 0; i < this.anchorsWithFanzone?.length; i++) {
      if (anchorId === this.anchorsWithFanzone[i]?.id) {
        return true;
      }
    }

    return false;
  };

  buildAnchorLink = (anchor) => {
    const nameArray = anchor.name.split(' ');
    let finalName = '';
    for (let i = 0; i < nameArray.length; i++) {
      finalName += `${nameArray[i]}-`;
    }

    return `${BASE_ROUTE}-${finalName.toLowerCase()}a${anchor.id}${
      this.anchorHasFanzone(anchor?.id) ? '?filter=fanzone' : ''
    }`;
  };
}

export default new AnchorStore();
